import moment from "moment"
import {BasePopup} from "components/popup"
import {useMemo, useState} from "react"
import {useQuery} from "@tanstack/react-query"
import {generalSettingsService} from "services"
import {GeneralSettingField, DailyAttendanceTimeScale} from "types/settings/general-setting"
import {YesNo} from "types/common"
import {DailyAttendance} from "../../../Attendance/parts/DailyAttendance/DailyAttendance"
import {DAILY_ATTENDANCE_TABS} from "../../../Attendance/parts/DailyAttendance/DailyAttendanceTab"
import {FilterKey} from "types/filter"
import styles from "./AttendanceIssues.module.css"

export enum AttendanceErrorLogType {
    SyncError = "isSyncError",
    OverlappingTime = "isOverlappingTime",
    MissingCheckin = "isMissingCheckin",
    MissingCheckout = "isMissingCheckout",
    HasTriangleAlert = "hasTriangleAlert"
}

export type AttendanceIssueItem = {
    icon: any
    label: string
    count: number
    color: string
    attendanceErrorLogType?: AttendanceErrorLogType
}

type FameSettings = {
    fameAdvantageEnabled: boolean
    fameFreedomEnabled: boolean
}

type Props = {
    isShow: boolean
    onClose: () => void
    activeType?: AttendanceErrorLogType
    activeLabel?: string
}

export function AttendanceErrorLogTablePopup(props: Props) {
    const {isShow, onClose, activeType, activeLabel} = props

    const [dateRange, setDateRange] = useState<string[]>([])

    const overrideFilterParams = useMemo(() => {
        if (!activeType) return undefined
        return {
            fromDate: undefined,
            toDate: undefined,
            includeDeletedAttendance: false,
            [activeType]: true
        }
    }, [activeType])

    const {data: fameSettings} = useQuery(
        ["fame-advantage-setting-attendance-error-log-popup"],
        async () => {
            const result = await generalSettingsService.getSettings([
                GeneralSettingField.FameAdvantageEnabled,
                GeneralSettingField.FameFreedomEnabled,
                GeneralSettingField.DailyAttendanceTimeScale
            ])
            const findByField = (field: GeneralSettingField) => result.find((item) => item.field === field)?.value

            return {
                fameAdvantageEnabled: findByField(GeneralSettingField.FameAdvantageEnabled) === YesNo.Yes,
                fameFreedomEnabled: findByField(GeneralSettingField.FameFreedomEnabled) === YesNo.Yes,
                dailyAttendanceTimeScale:
                    findByField(GeneralSettingField.DailyAttendanceTimeScale) ?? DailyAttendanceTimeScale.Minutes
            }
        },
        {
            initialData: {
                fameAdvantageEnabled: false,
                fameFreedomEnabled: false,
                dailyAttendanceTimeScale: DailyAttendanceTimeScale.Minutes
            }
        }
    )

    return (
        <BasePopup isShow={isShow} onClose={onClose} leftIcon="PERSON_FILL" width="90vw">
            <div className={styles.popupWrapper}>
                <p className={styles.popupTitle}>Attendance{activeLabel ? ` ${activeLabel}` : ""}</p>
                <div className={styles.divisor} />
                {activeType ? (
                    <DailyAttendance
                        key={activeType}
                        type={DAILY_ATTENDANCE_TABS.ALL}
                        statsInfo={[]}
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        filter={{
                            phaseIds: [],
                            shiftIds: [],
                            campusIds: [],
                            programIds: [],
                            eventType: undefined
                        }}
                        isErrorLog
                        defaultStudentStatuses={[]}
                        hideDateFilter
                        fameSettings={fameSettings as FameSettings}
                        showAll
                        filterKeyOverride={FilterKey.DailyAttendanceErrorLogPopup}
                        showCheckedColumn={false}
                        overrideFilterParams={overrideFilterParams}
                    />
                ) : null}
            </div>
        </BasePopup>
    )
}
